// src/utils/googleDrivePublish.js

const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";

function ensureGsiLoaded() {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (window.google?.accounts?.oauth2) {
        clearInterval(t);
        resolve();
      }
      if (tries > 50) { // ~5s
        clearInterval(t);
        reject(new Error("Google Identity Services not loaded. Check index.html script tag."));
      }
    }, 100);
  });
}

export async function getGoogleDriveAccessToken(clientId) {
  await ensureGsiLoaded();

  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      include_granted_scopes: true,
      prompt: "consent",
      callback: (resp) => {
        if (resp?.access_token) resolve(resp.access_token);
        else reject(new Error(resp?.error || "Failed to get Google access token"));
      },
    });

    tokenClient.requestAccessToken();
  });
}

function buildMultipartBody({ metadata, fileBlob, fileMimeType }) {
  const boundary = "----sow_boundary_" + Math.random().toString(16).slice(2);
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelim = `\r\n--${boundary}--`;

  const metaPart =
    delimiter +
    "Content-Type: application/json; charset=UTF-8\r\n\r\n" +
    JSON.stringify(metadata);

  const fileHeader =
    delimiter +
    `Content-Type: ${fileMimeType}\r\n\r\n`;

  const body = new Blob(
    [metaPart, fileHeader, fileBlob, closeDelim],
    { type: `multipart/related; boundary=${boundary}` }
  );

  return { body, contentType: `multipart/related; boundary=${boundary}` };
}

export async function uploadDocxAsGoogleDoc({
  accessToken,
  docxBlob,
  docTitle,
  folderId,          // optional
  supportsAllDrives, // true if folder can be in shared drive
}) {
  const metadata = {
    name: docTitle || "SOW Draft",
    mimeType: "application/vnd.google-apps.document",
  };

  const fid = (folderId || "").trim();
  if (fid) metadata.parents = [fid];

  const { body, contentType } = buildMultipartBody({
    metadata,
    fileBlob: docxBlob,
    fileMimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  const params = new URLSearchParams({
    uploadType: "multipart",
    fields: "id,webViewLink",
  });

  if (supportsAllDrives) params.set("supportsAllDrives", "true");

  const url = `https://www.googleapis.com/upload/drive/v3/files?${params.toString()}`;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": contentType,
      "X-Upload-Content-Type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    },
    body,
  });

  const json = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    const apiMsg = json?.error?.message || "";
    let msg = apiMsg || `Drive upload failed (HTTP ${resp.status})`;

    if (apiMsg.toLowerCase().includes("file not found")) {
      msg =
        "Drive folder not accessible (File not found).\n" +
        "Fix: verify folderId is correct and you have access to that folder with the same signed-in Google account.\n" +
        "Tip: try leaving folderId blank to publish in My Drive root.";
    }

    throw new Error(msg);
  }

  const id = json.id;
  const webViewLink = json.webViewLink || (id ? `https://docs.google.com/document/d/${id}/edit` : "");
  return { id, webViewLink };
}
